// DEPENDENCIES
import { FormikProps, FormikValues } from "formik";
import { getFirestore } from "firebase/firestore";
import * as Sentry from "@sentry/react";

// TYPES
import { DraftUUID, Step, StepData } from "../../types";
import { AppDispatch } from "../../app/store";
import { InfoStepType } from "../../reducers/infoStepReducer";

// UTILS
import {
  checkIsEmailAvailable,
  contactBlacklistSupport,
  deleteDniImages,
  deleteLiveness,
  genericToastifyError,
  getCompanyData,
  getDniFromCuit,
  getInfoStepData,
} from "../../utils";
import { showToastMessage } from "@almafintech/react-components/ToastMessage";
import {
  AuthReducer,
  updateSignatureRequestId,
  updateToken,
} from "../../reducers/authReducer";
import { goToStep, isTokenActive } from "../../app/utils";
import {
  onUpdateCompletedStep,
  onUpdateDraftUUID,
  onUpdateFormData,
  onUpdateReachedStep,
  onUpdateStep,
  onUpdateSubStep,
} from "../../reducers/onboardingDataReducer";
import { authEmail, deleteIndividualDraft } from "../../connectors/connectors";
import { logAnalyticsAndHotjarEvent } from "../../main";

// COMPONENTS
import InteractiveInformation from "../../components/InteractiveInformation/InteractiveInformation";

const infoStep: Step = {
  step: "information-step",
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    const email = formik?.values?.email;
    if (email) Sentry.setUser({ email });

    const { draftUUID, stepData } = state.onboardingData;
    const { type: infoStepType } = state.infoStep;
    const authentication = state.authentication;

    const onPrimaryClick = () => {
      try {
        checkToken(dispatch, formik, authentication);
        if (infoStepType === "BLACKLIST") {
          contactBlacklistSupport();
        } else {
          resumeDraft(dispatch, formik, infoStepType, stepData);
        }
      } catch (error) {
        console.log(error);
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "error",
        });
      }
    };

    const onSecondaryClick = async () => {
      try {
        checkToken(dispatch, formik, authentication);
        restartOrResumeDraft(
          dispatch,
          formik,
          infoStepType,
          draftUUID,
          authentication
        );
      } catch (error) {
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "error",
        });
      }
    };

    const { alertType, title, subtitle, primaryText, secondaryText } =
      getInfoStepData(infoStepType, "PHYSICAL");

    return (
      <InteractiveInformation
        alertType={alertType}
        title={title}
        subtitle={subtitle}
        primaryButton={{
          text: primaryText,
          onClick: onPrimaryClick,
        }}
        secondaryButton={{
          text: secondaryText,
          onClick: onSecondaryClick,
        }}
      />
    );
  },
  hideLayoutButtons: true,
  hideStepper: true,
};

const checkToken = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  authentication: AuthReducer
) => {
  const { token } = authentication;

  const email = formik.values?.email;
  const cuit = formik.values?.cuit;

  if (token && !isTokenActive({ token })) {
    dispatch({ type: "reset-store" });
    dispatch(onUpdateFormData({ email, cuit }));
    dispatch(onUpdateCompletedStep(1));
    dispatch(updateToken(token));

    formik?.resetForm({ values: { email, cuit } });
    const res = await authEmail({
      email,
      companyType: getCompanyData().companyName,
    });
    dispatch(updateSignatureRequestId(res.data.signatureRequestId));
    dispatch(onUpdateStep(2));
  }
};

const resumeDraft = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  infoStep: InfoStepType,
  stepData: StepData
) => {
  const { values } = formik;

  const cuit = values?.cuit;

  switch (infoStep) {
    case "ACTIVE_ACCOUNT":
    case "REGISTERED_EMAIL":
      goToStep({ dispatch, step: 3, substep: 1 });
      break;
    case "PENDING_REGISTRY_REMOTE":
    case "PENDING_REGISTRY_LOCAL": {
      if (values.identityDocument) {
        checkIsEmailAvailable({
          dispatch,
          idNumber: values.identityDocument,
        });
      }

      const { nextStep, nextSubStep } = getNextStepAndSubStep(
        stepData,
        values,
        infoStep
      );

      //When the user reaches step 3 (even if he does not complete the data) we should not take him to step 1 in case he refreshes the page.
      //So we save the identityDocument in the store to avoid this.
      dispatch(
        onUpdateFormData({
          identityDocument: getDniFromCuit(cuit),
        })
      );

      dispatch(onUpdateStep(nextStep));
      dispatch(onUpdateSubStep(nextSubStep));
      if (infoStep === "PENDING_REGISTRY_REMOTE") {
        dispatch(onUpdateReachedStep(nextStep));
        dispatch(onUpdateCompletedStep(nextStep - 1));
      }
      break;
    }
    case null:
      window.location.pathname = "/empresarial";
      break;
    default:
      break;
  }
  logAnalyticsAndHotjarEvent("onboarding_resumed");
};

const restartOrResumeDraft = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  infoStep: InfoStepType,
  draftUUID: DraftUUID,
  authentication: AuthReducer
) => {
  const { token, signatureRequestId } = authentication;

  switch (infoStep) {
    case "PENDING_REGISTRY_REMOTE":
    case "PENDING_REGISTRY_LOCAL":
      {
        const { cuit, email, referredBool, referrer, identityDocument } =
          formik.values;

        const tempValues = {
          token,
          signatureRequestId,
          cuit,
          email,
          referredBool,
          referrer,
          draftUUID,
          identityDocument,
        };

        const db = getFirestore();

        await deleteIndividualDraft({ draftUUID });
        await deleteDniImages(db, tempValues.draftUUID);
        await deleteLiveness(db, tempValues.draftUUID);

        dispatch({ type: "reset-store" });

        if (infoStep === "PENDING_REGISTRY_REMOTE") {
          formik.resetForm({
            values: {
              cuit: tempValues.cuit,
              email: tempValues.email,
              referrer: tempValues.referrer,
              referredBool: tempValues.referredBool,
              identityDocument:
                tempValues.identityDocument || getDniFromCuit(tempValues.cuit),
            },
          });

          dispatch(onUpdateDraftUUID(tempValues.draftUUID));
          dispatch(updateSignatureRequestId(tempValues.signatureRequestId));
          dispatch(updateToken(tempValues.token));
          goToStep({ dispatch, step: 3 });
        } else {
          formik.resetForm();
          dispatch(onUpdateStep(1));
          dispatch(onUpdateReachedStep(1));
        }
        logAnalyticsAndHotjarEvent("onboarding_restart");
      }
      break;
    case null:
    case "ACTIVE_ACCOUNT":
    case "BLACKLIST":
      dispatch(onUpdateStep(1));
      dispatch(onUpdateReachedStep(1));
      break;
    default:
      break;
  }
};

const getNextStepAndSubStep = (
  stepData: StepData,
  values: FormikValues,
  infoStep: InfoStepType
) => {
  let nextStep = 1;
  let nextSubStep = 1;

  if (infoStep === "PENDING_REGISTRY_LOCAL") {
    const { reachedSteps, lastCompletedSubStep } = stepData;
    if (reachedSteps) {
      nextStep = reachedSteps[reachedSteps.length - 1];
      nextSubStep = lastCompletedSubStep + 1;

      // Send to the last step even if it has been completed
      if (nextStep === 7) {
        nextStep = 6;
        if (nextSubStep === 3) {
          nextSubStep = 2;
        }
      }

      return { nextStep, nextSubStep };
    }
  }

  const { telephone, employmentProfile, investmentProfile, gender } = values;

  // STEP THREE
  const isFirstSubstepInStepThreeCompleted = gender !== "";
  if (isFirstSubstepInStepThreeCompleted) {
    nextStep = 3;
    nextSubStep = 2;
  }

  // STEP FOUR
  const isFirstSubstepInStepFourCompleted = telephone !== "";
  if (isFirstSubstepInStepFourCompleted) {
    nextStep = 4;
    nextSubStep = 2;
  }

  // STEP FIVE
  const isStepFiveCompleted =
    employmentProfile && employmentProfile?.employmentStatus !== "";
  if (isStepFiveCompleted) {
    nextStep = 6;
    nextSubStep = 1;
  }

  // STEP SIX
  const isFirstSubstepInStepSixCompleted =
    investmentProfile &&
    Object.values(investmentProfile).filter((value) => value !== "").length > 0;
  if (isFirstSubstepInStepSixCompleted) {
    nextStep = 6;
    nextSubStep = 2;
  }

  return { nextStep, nextSubStep };
};

export default infoStep;
